import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import {
  enumSalesOrderSortOption,
  enumSalesOrderStatus,
} from '@rematter/frontend-lib-api-client';
import { cn, MultiSelect } from '@rematter/ui-web';
import { atom, useAtomValue } from 'jotai';
import concat from 'lodash/concat';
import differenceBy from 'lodash/differenceBy';
import uniqBy from 'lodash/uniqBy';

import { salesOrdersListQueryAtom } from '../../_graphql/querySalesOrdersList';

import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';
import { formatSalesOrderLabel } from '~/shared/utils/salesOrderFormatting';

export type SalesOrderOption = {
  id: string;
  documentNumber: string;
  account?: { name?: string | null } | null;
  startDate?: CalendarDate | null;
  endDate?: CalendarDate | null;
};

export type SalesOrderSelectProps = {
  isRequired?: boolean;
  label?: string;
  placeholder?: string;
  onSelectionChange?: (entries: SalesOrderOption[]) => void;
  selectedEntries?: SalesOrderOption[];
  className?: string;
  isDisabled?: boolean;
  accountIds?: string[];
};

export const SalesOrdersMultiSelect = ({
  isRequired = false,
  label = 'SO(s) #',
  placeholder = 'Select Sales Orders',
  selectedEntries = [],
  onSelectionChange,
  className,
  isDisabled = false,
  accountIds,
}: SalesOrderSelectProps) => {
  const salesOrderFieldRef = React.useRef<HTMLDivElement>(null);

  const [$salesOrderSearchTerm] = React.useState(() => atom(''));

  const $salesOrders = React.useMemo(
    () =>
      salesOrdersListQueryAtom((get) => {
        const searchTerm = get($salesOrderSearchTerm);
        return {
          variables: {
            searchTerm: searchTerm,
            limit: 6,
            offset: 0,
            sorting: [enumSalesOrderSortOption.CreatedAtDesc],
            status: [enumSalesOrderStatus.Open, enumSalesOrderStatus.Finished],
            accountIds: accountIds,
            includeLineItems: false,
          },

          skip: false,
        };
      }),
    [$salesOrderSearchTerm, accountIds],
  );

  const { data } = useAtomValue($salesOrders);
  const [searchTerm, setSearchTerm] = useDebouncedAtom(
    $salesOrderSearchTerm,
    300,
  );

  const salesOrdersOptions = React.useMemo(() => {
    if (!searchTerm) {
      const difference = differenceBy(
        selectedEntries,
        data?.salesOrders ?? [],
        'id',
      );
      if (difference.length > 0) {
        return uniqBy(concat(difference, data?.salesOrders), 'id')
          .filter((entry) => entry !== undefined)
          .map((entry) => ({
            key: entry.id,
            label: formatSalesOrderLabel(entry),
          }));
      }
    }
    return uniqBy(concat(data?.salesOrders, selectedEntries), 'id')
      .filter((entry) => entry !== undefined)
      .map((entry) => ({
        key: entry.id,
        label: formatSalesOrderLabel(entry),
      }));
  }, [data?.salesOrders, selectedEntries, searchTerm]);

  const handleSelectionChange = React.useCallback(
    (keys: string[]) => {
      onSelectionChange?.(
        keys.map((key) => ({
          id: key,
          documentNumber:
            salesOrdersOptions.find((entry) => entry.key === key)?.label ?? '',
        })),
      );
    },
    [onSelectionChange, salesOrdersOptions],
  );

  React.useEffect(
    () => () => {
      setSearchTerm('');
    },
    [setSearchTerm],
  );

  return (
    <MultiSelect
      ref={salesOrderFieldRef}
      label={label}
      placeholder={placeholder}
      options={salesOrdersOptions}
      onSelectionChange={handleSelectionChange}
      selectedKeys={selectedEntries.map((entry) => entry.id)}
      className={cn('@SalesOrdersMultiSelect', className)}
      onSearch={(query) => {
        setSearchTerm(query);
      }}
      required={isRequired}
      isDisabled={isDisabled}
    />
  );
};
